import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiHome, FiMap, FiBookOpen, FiClipboard, FiAward, FiZap, FiEdit3, FiPlayCircle, FiMessageCircle, FiMoreHorizontal } from 'react-icons/fi';
import { VscGame } from 'react-icons/vsc';
import DuelIcon from '@/components/custom/icons/DuelIcon';
import IconExams from '@/components/custom/icons/IconExams';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { PATHS } from '@/routes/paths';
import tiaAvatar from '@/assets/images/tia/tia-avatar-3d.png';
import { TiaChatBar } from './tia-chat-bar';
import { TiaChatSheet } from './tia-chat-sheet';
import { useTiaChat, getUserLearningPathIdFromPathname } from './use-tia-chat';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';

export function getActiveKey(pathname) {
  if (pathname === '/') return 'inicio';
  if (pathname.startsWith('/trilha')) return 'trilha';
  if (pathname.startsWith('/desafio-da-semana')) return 'desafio-semana';
  if (pathname.startsWith('/duelos')) return 'duelos';
  if (pathname.startsWith('/curso')) return 'cursos';
  if (pathname.startsWith('/redacao')) return 'redacao';
  if (pathname.startsWith('/simulados')) return 'simulado';
  if (pathname.startsWith('/avaliacoes')) return 'provas';
  if (pathname.startsWith('/usuario/medalhas')) return 'conquistas';
  if (pathname.startsWith('/desafio-ao-vivo')) return 'desafios';
  if (pathname.startsWith('/aulas-ao-vivo')) return 'aulas-ao-vivo';
  if (pathname.startsWith('/questions')) return 'exercicios';
  if (pathname.startsWith('/feed')) return 'forum';
  return null;
}

export function buildTabs(userId, abilities) {
  return [
    { key: 'inicio', label: 'Início', to: PATHS.DASHBOARD.ROOT, Icon: FiHome, visibility: true },
    { key: 'trilha', label: 'Trilha', to: PATHS.LEARNING_PATH.ROOT, Icon: FiMap, visibility: !!abilities?.['plano-de-estudo'] },
    { key: 'desafio-semana', label: 'Desafios', to: PATHS.WEEK_CHALLENGE.LIST, Icon: VscGame, visibility: !!abilities?.['desafio-da-semana'] },
    { key: 'duelos', label: 'Duelos', to: PATHS.DUELS.LIST, Icon: DuelIcon, visibility: !!abilities?.duelos },
    { key: 'cursos', label: 'Cursos', to: PATHS.COURSE.LIST, Icon: FiBookOpen, visibility: !!abilities?.cursos },
    { key: 'redacao', label: 'Redação', to: PATHS.ESSAY.LIST, Icon: FiEdit3, visibility: !!abilities?.redacao },
    { key: 'simulado', label: 'Simulado', to: PATHS.EXAM.MOCK_LIST, Icon: FiClipboard, visibility: !!abilities?.['avaliacoes-simulados'] },
    { key: 'provas', label: 'Provas', to: PATHS.EXAM.LIST, Icon: IconExams, visibility: !!(abilities?.['avaliacoes-simulados'] || abilities?.['avaliacoes-provas']) },
    { key: 'exercicios', label: 'Exercícios', to: PATHS.QUESTIONS.TAB('apostilas'), Icon: FiEdit3, visibility: !!abilities?.['avaliacoes-apostilas'] },
    { key: 'conquistas', label: 'Conquistas', to: PATHS.PROFILE.USER('medalhas', userId), Icon: FiAward, visibility: true },
    { key: 'desafios', label: 'Ao vivo', to: PATHS.LIVE_CHALLENGE.JOIN, Icon: FiZap, visibility: !!abilities?.['desafio-ao-vivo'] },
    { key: 'aulas-ao-vivo', label: 'Aula ao vivo', to: PATHS.LIVE_CLASSES.LIST, Icon: FiPlayCircle, visibility: !!abilities?.['aulas-ao-vivo'] },
    { key: 'forum', label: 'Fórum', to: PATHS.FEED.LIST, Icon: FiMessageCircle, visibility: !!abilities?.feed }
  ];
}

const EXIT_ANIMATION_MS = 180; // espelha tia-bar-out/tia-sheet-out no sass

// Mantém o elemento montado durante a animação de saída antes de desmontar.
function usePresence(open, exitMs = EXIT_ANIMATION_MS) {
  const [shouldRender, setShouldRender] = React.useState(open);

  React.useEffect(() => {
    if (open) {
      setShouldRender(true);
      return undefined;
    }
    const timer = setTimeout(() => setShouldRender(false), exitMs);
    return () => clearTimeout(timer);
  }, [open, exitMs]);

  return { shouldRender, closing: !open && shouldRender };
}

export function BottomBarInfantil() {
  const { session } = useSession();
  const { pathname } = useLocation();
  const { abilities } = usePlatformAbilities();
  const userId = session?.user?.id;
  const activeKey = getActiveKey(pathname);
  const tabs = buildTabs(userId, abilities).filter(tab => tab.visibility);
  const tabsMiddle = Math.ceil(tabs.length / 2);

  // Mobile: 7 itens com a T.I.A no centro geométrico — 3 abas à esquerda dela,
  // 2 abas + "Ver mais" à direita; o excedente abre num bottom sheet.
  // No sm+ todas as abas voltam pra barra e o Ver mais some.
  const MOBILE_LEFT_TABS = 3;
  const MOBILE_RIGHT_TABS = 2;
  const leftTabs = tabs.slice(0, tabsMiddle);
  const rightTabs = tabs.slice(tabsMiddle);
  const overflowTabs = [...leftTabs.slice(MOBILE_LEFT_TABS), ...rightTabs.slice(MOBILE_RIGHT_TABS)];
  const moreActive = overflowTabs.some(tab => tab.key === activeKey);
  const [moreOpen, setMoreOpen] = React.useState(false);

  const [chatOpen, setChatOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const barPresence = usePresence(chatOpen);
  const sheetPresence = usePresence(sheetOpen);
  const chat = useTiaChat({ userLearningPathId: getUserLearningPathIdFromPathname(pathname) });
  const hasLoadedHistory = React.useRef(false);

  const closeChat = () => {
    setChatOpen(false);
    setSheetOpen(false);
  };

  const openChat = () => {
    setChatOpen(open => {
      const next = !open;
      if (!next) setSheetOpen(false);
      return next;
    });
    if (!hasLoadedHistory.current) {
      hasLoadedHistory.current = true;
      chat.loadHistory();
    }
  };

  const renderTab = ({ key, label, to, Icon }, hideOnMobile) => {
    const isActive = activeKey === key;
    return (
      <Link
        key={key}
        to={to}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          // Todas as abas com a MESMA largura (flex-1) — trocar de ativa não
          // desloca o layout. Mobile: só ícone (rótulo não cabe em 7 abas a
          // 390px); o estado ativo muda só cor + fundo. Rótulo volta no sm+.
          'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1.5 text-center text-[11px] font-semibold transition-colors max-[430px]:text-[10px]',
          isActive ? 'text-secondary' : 'text-neutral-dark',
          // Fora da cota mobile da sua fatia, o acesso é pelo "Ver mais".
          hideOnMobile && 'max-sm:hidden'
        )}
        style={isActive ? { background: 'color-mix(in srgb, var(--color-secondary) 16%, #fff)' } : undefined}
      >
        {/* Ícones menores no mobile (17px) — 7 itens pedem mais respiro; sm+ volta aos 20px. */}
        <Icon
          aria-hidden="true"
          className="h-[17px] w-[17px] sm:h-5 sm:w-5"
        />
        <span className="whitespace-nowrap leading-none max-sm:hidden">{label}</span>
      </Link>
    );
  };

  return (
    <>
      {sheetPresence.shouldRender && (
        <TiaChatSheet
          messages={chat.messages}
          status={chat.status}
          onRetry={chat.retry}
          onClose={() => setSheetOpen(false)}
          closing={sheetPresence.closing}
        />
      )}
      {/* md:max-w-3xl: teto fixo p/ 9 abas (~82px/aba, densidade do design original);
          70vw escalava com o monitor e passava de 1000px em telas largas. */}
      <div className="fixed bottom-[calc(1.75rem+env(safe-area-inset-bottom))] left-1/2 z-[103] w-[calc(100%-1rem)] max-w-xl -translate-x-1/2 sm:w-[calc(100%-2rem)] md:max-w-3xl">
        <nav
          className={cn('c-bottom-bar-infantil', 'flex items-stretch', 'rounded-full border border-neutral-lighter bg-white px-2 py-1.5', 'shadow-[0_12px_32px_rgba(0,0,0,0.16)]')}
          aria-label="Navegação principal"
        >
          {leftTabs.map((tab, i) => renderTab(tab, i >= MOBILE_LEFT_TABS))}
          {abilities?.['tia-chat'] && (
            <button
              type="button"
              onClick={openChat}
              aria-label="Conversar com a T.I.A"
              aria-expanded={chatOpen}
              aria-controls="tia-chat-sheet"
              // A raia estreita no mobile libera largura pros labels; o avatar
              // (shrink-0, flutuando acima da barra) transborda sem colidir.
              className="flex w-10 flex-none items-start justify-center sm:w-auto sm:flex-1"
            >
              <img
                src={tiaAvatar}
                alt=""
                // max-w-none: o CSS global de img (max-width:100%) capava o w-16
                // dentro da raia w-10 e esmagava o círculo em pílula 40×64.
                className={cn('-mt-8 h-16 w-16 max-w-none shrink-0 rounded-full border-4 border-white bg-white shadow-lg', 'transition-transform active:scale-95', chatOpen && 'ring-2 ring-secondary')}
              />
            </button>
          )}
          {rightTabs.map((tab, i) => renderTab(tab, i >= MOBILE_RIGHT_TABS))}
          {overflowTabs.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              className={cn(
                // Mesma largura fixa (flex-1) das abas; só ícone no mobile.
                'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1.5 text-center text-[11px] font-semibold transition-colors max-[430px]:text-[10px] sm:hidden',
                moreActive || moreOpen ? 'text-secondary' : 'text-neutral-dark'
              )}
              style={moreActive || moreOpen ? { background: 'color-mix(in srgb, var(--color-secondary) 16%, #fff)' } : undefined}
            >
              <FiMoreHorizontal
                aria-hidden="true"
                className="h-[17px] w-[17px]"
              />
              <span className="hidden">Ver mais</span>
            </button>
          )}
        </nav>
        {barPresence.shouldRender && (
          <TiaChatBar
            onSubmit={body => {
              setSheetOpen(true);
              chat.send(body);
            }}
            onFocus={() => {
              if (chat.messages.length > 0) setSheetOpen(true);
            }}
            onClose={closeChat}
            disabled={chat.status === 'thinking' || chat.status === 'loading'}
            closing={barPresence.closing}
          />
        )}
      </div>
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-[110] bg-black/30"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Mais acessos"
            className="fixed inset-x-0 bottom-0 z-[111] rounded-t-3xl bg-white px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-12px_32px_rgba(0,0,0,0.16)]"
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-neutral-lighter" />
            <p className="mb-4 text-sm font-bold text-neutral-dark">Mais acessos</p>
            <div className="grid grid-cols-3 gap-3">
              {overflowTabs.map(({ key, label, to, Icon }) => {
                const isActive = activeKey === key;
                return (
                  <Link
                    key={key}
                    to={to}
                    onClick={() => setMoreOpen(false)}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn('flex flex-col items-center gap-2 rounded-2xl border px-2 py-4 text-center text-[12px] font-semibold transition-colors', isActive ? 'border-secondary/30 text-secondary' : 'border-neutral-lighter text-neutral-dark')}
                    style={isActive ? { background: 'color-mix(in srgb, var(--color-secondary) 12%, #fff)' } : undefined}
                  >
                    <Icon
                      size={22}
                      aria-hidden="true"
                    />
                    <span className="leading-none">{label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
